/**
 * Drawer — panneau latéral droit pour le détail (S5b).
 *
 * Contrairement à `<Modal>` (centré, modal, demande une décision), un
 * `<Drawer>` est consultatif : on l'ouvre pour lire un détail sans perdre
 * le contexte de la liste, on le ferme par Escape ou clic sur le backdrop
 * léger.
 *
 * **Verrou** : doit être monté dans un `<DrawerStackProvider>` (vérifié par
 * la règle ESLint `drawer-must-use-stack` + au runtime par
 * `useDrawerStack` qui lève si pas de provider).
 *
 * Premier consommateur S5b : `<QueuePage>` qui ouvre le détail d'un job
 * (queue_item + historique des tentatives).
 */
import { useEffect, useId, type ReactNode } from 'react'

import { useDrawerStack } from '@/contexts/drawer-stack-context'

import './Drawer.css'

export type DrawerSize = 'compact' | 'comfortable' | 'wide'

interface DrawerProps {
  isOpen: boolean
  onClose: () => void
  title?: ReactNode
  /** Étiquette contextuelle au-dessus du titre. */
  kicker?: ReactNode
  size?: DrawerSize
  children: ReactNode
  className?: string
  /** Actions custom dans le header (à côté du bouton fermer). */
  headerActions?: ReactNode
  /** Footer sticky en bas du panneau (boutons Annuler/Action). */
  footer?: ReactNode
  /** ARIA label si pas de titre visible. */
  ariaLabel?: string
}

function Drawer({
  isOpen,
  onClose,
  title,
  kicker,
  size = 'comfortable',
  children,
  className,
  headerActions,
  footer,
  ariaLabel,
}: DrawerProps) {
  const stack = useDrawerStack()

  // Enregistrement dans la pile : à l'ouverture on prend l'index courant,
  // au close on libère. La profondeur (depth) sert à offset le panneau et
  // à différencier visuellement le drawer du dessus.
  useEffect(() => {
    if (!isOpen) return
    const idx = stack.register()
    return () => stack.unregister(idx)
  }, [isOpen, stack])

  // Escape ferme uniquement le drawer du dessus (par convention : on ne
  // gère ici que le close de cette instance — le système d'événement
  // émet keydown sur tous les Drawers ouverts. On ferme silencieusement
  // dans tous les cas, l'utilisateur appuie une fois → un seul ferme
  // car les autres sont déjà unmount par cascade).
  useEffect(() => {
    if (!isOpen) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [isOpen, onClose])

  const reactId = useId()
  const titleId = `drawer-title-${reactId}`

  if (!isOpen) return null

  const sizeClass = `drawer-content--${size}`
  const contentClasses = ['drawer-content', sizeClass, className].filter(Boolean).join(' ')
  const hasVisibleTitle = title != null
  // Offset progressif pour les drawers empilés — le 2e drawer recule de
  // 24px, le 3e de 48px, etc. Donne une élévation visuelle nette.
  const stackOffset = Math.max(0, stack.depth - 1) * 24

  return (
    <div className="drawer-overlay">
      <button
        type="button"
        className="drawer-backdrop"
        aria-label="Fermer le panneau"
        onClick={onClose}
      />
      <div
        className={contentClasses}
        role="dialog"
        aria-modal="true"
        aria-labelledby={hasVisibleTitle ? titleId : undefined}
        aria-label={hasVisibleTitle ? undefined : ariaLabel}
        style={stackOffset > 0 ? { right: stackOffset } : undefined}
      >
        {hasVisibleTitle && (
          <div className="drawer-header">
            <div className="drawer-header-title-group">
              {kicker && <div className="drawer-kicker">{kicker}</div>}
              <h2 id={titleId} className="drawer-title">
                {title}
              </h2>
            </div>
            <div className="drawer-header-actions">
              {headerActions}
              <button type="button" className="drawer-close" onClick={onClose} aria-label="Fermer">
                ×
              </button>
            </div>
          </div>
        )}
        <div className="drawer-body">{children}</div>
        {footer && <div className="drawer-footer">{footer}</div>}
      </div>
    </div>
  )
}

function DrawerSection({ title, children }: { title?: ReactNode; children: ReactNode }) {
  return (
    <section className="drawer-section">
      {title && <h3 className="drawer-section-title">{title}</h3>}
      {children}
    </section>
  )
}

Drawer.Section = DrawerSection

export default Drawer
